import EasySax from 'easysax'
import HTML_TAGS from '../constants/html-tags'
import debug from '../core/debug'
import { includes, isObject, last, map } from '../libraries/lodash'
import { withDefaults } from './lodash'

const htmlTags = withDefaults(HTML_TAGS, true)

const toAttributes = function (attr) {
  if (!isObject(attr)) {
    return ''
  }

  return map(attr, (value, key) => `data-${key}='${value}'`).join(' ')
}

const selfClosingTags = [
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'keygen',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr'
]

const handleParserError = function (stack, err) {
  debug.warn(
    `XML parse error: ${err}, path: ${stack
      .map(s => s.name)
      .join(' > ')}, stack: `,
    stack
  )
}

export const toHtml = function (
  xmlStr,
  tagName = 'span',
  convertAllTags = false,
  ignore = []
) {
  let output = ''
  const parser = new EasySax()
  const stack = []
  let pageWidth

  parser.on('error', err => handleParserError(stack, err))
  parser.on('startNode', (elem, attr, uq, str, tag) => {
    stack.push(elem)
    if (ignore.indexOf(elem) > -1) {
      // do nothing
    } else if (elem === 'glossary') {
      output += `<${tagName} class='fc-${elem}' data-popover-content='${
        attr().definition
      }'>`
    } else if (elem === 'cross-reference') {
      output += `<a class='fc-${elem}' ${toAttributes(attr())}>`
    } else if (elem === 'colgroup') {
      output += '<colgroup>'
      pageWidth = +attr().pageWidth
    } else if (elem === 'col') {
      const width = +attr().width
      output += `<col width='${(100 * width / pageWidth).toFixed(2)}%'>`
    } else if (!convertAllTags && elem in htmlTags) {
      output += tag()
    } else {
      output += `<${tagName} class='fc-${elem}' ${toAttributes(attr())}>`
    }
  })
  parser.on('textNode', text => {
    output += text
  })
  parser.on('endNode', (elem, uq, str, tag) => {
    stack.pop()

    if (elem === 'col') return

    if (ignore.indexOf(elem) > -1) {
      // do nothing
    } else if (elem === 'cross-reference') {
      output += '</a>'
    } else if (!convertAllTags && elem in htmlTags) {
      output += tag()
    } else {
      output += `</${tagName}>`
    }
  })
  parser.parse(xmlStr)

  return output
}

export const toSectionHtml = function (xmlStr, tagName = 'div') {
  const parser = new EasySax()
  let output = ''
  let a
  const ALLOWED_TAGS = [
    'table',
    'tbody',
    'td',
    'tr',
    'th',
    'thead',
    'tfoot',
    'em',
    'externallink'
  ]
  const getTagName = elem => (includes(ALLOWED_TAGS, elem) && elem) || tagName

  parser.on('error', err => handleParserError([], err))
  parser.on('startNode', (elem, attr, uq, str, tag) => {
    if (elem === 'externallink' && (a = attr()) && a.href) {
      output += `<${getTagName(elem)} class='fc-${elem}' href=${a.href}>`
    } else {
      output += `<${getTagName(elem)} class='fc-${elem}'>`
    }

    if ((a = attr()) && a.ref) {
      output += `<span class='reference'>${a.ref}</span>`
    }
  })
  parser.on('textNode', text => {
    output += text
  })
  parser.on('endNode', (elem, uq, str, tag) => {
    output += `</${getTagName(elem)}>`
  })
  parser.parse(xmlStr)

  return output
}

export const toJson = function (xmlStr, parseNodes = null) {
  const parser = new EasySax()
  const output = { name: 'root', attributes: {}, childNodes: [] }
  const stack = [output]

  parser.on('error', err => handleParserError(stack, err))
  parser.on('startNode', (name, attr, uq, str, tag) => {
    const current = last(stack)
    if (parseNodes) {
      if (includes(parseNodes, name)) {
        const attributes = Object.assign({}, attr())
        const node = { name, attributes, childNodes: [] }
        current.childNodes.push(node)
        stack.push(node)
      } else {
        current.content = (current.content || '') + tag()
      }
    } else {
      const attributes = Object.assign({}, attr())
      const node = { name, attributes, childNodes: [] }
      current.childNodes.push(node)
      stack.push(node)
    }
  })
  parser.on('textNode', content => {
    const current = last(stack)
    if (parseNodes) {
      current.content = (current.content || '') + content
    } else {
      const node = { name: 'text', attributes: {}, content, childNodes: [] }
      current.childNodes.push(node)
    }
  })
  parser.on('endNode', (name, uq, str, tag) => {
    const current = last(stack)
    if (parseNodes) {
      if (includes(parseNodes, name)) {
        stack.pop()
      } else if (!includes(selfClosingTags, name)) {
        current.content = (current.content || '') + tag()
      }
    } else {
      stack.pop()
    }
  })
  parser.parse(xmlStr)
  return output.childNodes
}
